import {assign, createMachine, Machine, send, sendParent, spawn} from 'xstate';
import {createEntryListItemMachine} from './EntryListItemMachine.js';

const createFolderIndexMachine = (createOptions) => {
    const {methods, ...machineOptions} = createOptions;
    const {deleteEntry, toggleArchiveEntry, refreshEntryTitle} = methods;

    const mapNewEntry = (entry) => {
        return {
            data: entry,
            ref: spawn(createEntryListItemMachine({entry}))
        };
    };

    const mergeEntries = (entry, newFields) => {
        return {
            ...entry,
            data: {
                ...entry.data,
                ...newFields
            }
        }
    };

    const findEntryById = (entries, id) => {
        if (typeof id === 'string') {
            id = parseInt(id);
        }
        return entries.find((entry) => entry.data.id === id);
    };

    const deleteEntryMachine = Machine({
        id: 'delete-entry-machine',
        initial: 'invoking',
        context: {id: null},
        states: {
            invoking: {
                invoke: {
                    id: 'delete-entry',
                    src: (ctx) => deleteEntry(ctx),
                    onDone: 'done',
                    onError: 'error'
                }
            },
            done: {
                type: 'final',
                entry: sendParent((ctx) => ({
                    type: 'ENTRY.DELETE_SUCCESS',
                    ...ctx
                }))
            },
            error: {
                type: 'final',
                entry: sendParent((ctx) => ({
                    type: 'ENTRY.DELETE_FAILURE',
                    ...ctx
                }))
            }
        }
    });

    const archiveEntryMachine = Machine({
        id: 'archive-entry-machine',
        initial: 'invoking',
        context: {id: null, value: null},
        states: {
            invoking: {
                invoke: {
                    id: 'archive-entry',
                    src: (ctx, event) => {
                        return toggleArchiveEntry(ctx);
                    },
                    onDone: 'done',
                    onError: 'error'
                }
            },
            done: {
                type: 'final',
                entry: [
                    sendParent((ctx) => {
                        const type = ctx.value === true
                            ? 'ENTRY.ARCHIVED'
                            : 'ENTRY.UNARCHIVED';
                        return ({ type, ...ctx });
                    })
                ]
            },
            error: {
                type: 'final',
                entry: [
                    sendParent((ctx) => ({
                        type: 'ENTRY.TOGGLE_ARCHIVED_FAILURE',
                        ...ctx
                    }))
                ]
            }
        }
    });

    const refreshTitleMachine = Machine({
        id: 'refresh-entry-title-machine',
        initial: 'invoking',
        context: {id: null},
        states: {
            invoking: {
                invoke: {
                    id: 'refresh-entry-title',
                    src: (ctx, event) => {
                        return refreshEntryTitle(ctx);
                    },
                    onDone: 'done',
                    onError: 'error'
                }
            },
            done: {
                type: 'final',
                entry: sendParent((ctx, event) => ({
                    ...event,
                    type: 'ENTRY.REFRESH_TITLE_SUCCESS',
                    ...ctx,
                }))
            },
            error: {
                type: 'final',
                entry: sendParent((ctx) => ({
                    type: 'ENTRY.REFRESH_TITLE_FAILURE',
                    ...ctx
                }))
            }
        }
    });

    const mapNewFolders = (folders) => folders.map((f) => ({
        ...f,
        isBusy: false
    }));

    return createMachine({
        id: 'folder-index-machine',
        context: {
            entries: [],
            folders: [],
            errors: [],
            currentFolder: null,
            rootFolder: null,
            folderUUID: null,
            qrCodeEntry: null,
            editingEntry: null
        },
        initial: 'loading',
        states: {
            loading: {
                invoke: {
                    id: 'fetchCurrentFolder',
                    src: 'fetchCurrentFolder',
                    onDone: 'loaded',
                    onError: 'failure'
                }
            },
            failure: {
                initial: 'idle',
                entry: [
                    assign({
                        errors: () => ([{
                            type: 'danger',
                            text: 'Sorry, we weren\'t able to load this folder at this time. You can retry with the button below.'
                        }])
                    })
                ],
                states: {
                    idle: {
                        on: {
                            RETRY: {
                                target: 'retrying'
                            }
                        }
                    },
                    retrying: {
                        initial: 'pause',
                        states: {
                            pause: {
                                after: {
                                    500: 'executing'
                                }
                            },
                            executing: {
                                invoke: {
                                    id: 'fetchCurrentFolder',
                                    src: 'fetchCurrentFolder',
                                    onDone: '#folder-index-machine.loaded',
                                    onError: '#folder-index-machine.failure'
                                }
                            }
                        }
                    }
                }
            },
            loaded: {
                initial: 'idle',
                entry: [
                    assign({
                        entries: (ctx, {data}) => {
                            const {entries} = data;
                            return entries.map((entry) => mapNewEntry(entry));
                        },
                        folders: (ctx, {data}) => mapNewFolders(data.folders),
                        parent: (ctx, {data}) => data.parent,
                        currentFolder: (_, {data}) => data.currentFolder,
                        rootFolder: (_, {data}) => data.rootFolder,
                    }),
                    assign({
                        errors: []
                    })
                ],
                states: {
                    idle: {
                        on: {
                            'ROUTE_CHANGE': {
                                target: '#folder-index-machine.loading',
                                actions: assign({
                                    folderUUID: (ctx, {folderId}) => folderId,
                                    entries: [],
                                    folders: [],
                                    errors: [],
                                    currentFolder: null
                                })
                            },
                            'ENTRY.CREATED': {
                                actions: [
                                    assign({
                                        entries: (ctx, {entry}) => {
                                            return [
                                                mapNewEntry(entry),
                                                ...ctx.entries
                                            ];
                                        }
                                    }),
                                    'resultToast'
                                ]
                            },
                            'ENTRY.TOGGLE_ARCHIVED': {
                                actions: assign((_, {id, value}) => {
                                    spawn(
                                        archiveEntryMachine.withContext({id, value}),
                                        `archive-entry-${id}`
                                    );
                                })
                            },
                            'ENTRY.ARCHIVED': {
                                actions: [
                                    assign({
                                        entries: (ctx, {id, value}) => {
                                            return ctx.entries.map((entry) => {
                                                if (entry.data.id === id) {
                                                    return mergeEntries(entry, {
                                                        is_archived: value
                                                    });
                                                }
                                                return entry;
                                            });
                                        }
                                    }),
                                    send('IDLE', {
                                        to: (ctx, {id}) => {
                                            const entry = findEntryById(ctx.entries, id);
                                            return entry?.ref;
                                        }
                                    }),
                                    'resultToast'
                                ]
                            },
                            'ENTRY.UNARCHIVED': {
                                actions: [
                                    assign({
                                        entries: (ctx, {id, value}) => {
                                            return ctx.entries.map((entry) => {
                                                if (entry.data.id === id) {
                                                    return mergeEntries(entry, {
                                                        is_archived: value
                                                    });
                                                }
                                                return entry;
                                            });
                                        }
                                    }),
                                    send('IDLE', {
                                        to: (ctx, {id}) => {
                                            const entry = findEntryById(ctx.entries, id);
                                            return entry?.ref;
                                        }
                                    }),
                                    'resultToast'
                                ]
                            },
                            'ENTRY.TOGGLE_ARCHIVED_FAILURE': {
                                actions: [
                                    send('IDLE', {
                                        to: (ctx, {id}) => {
                                            const entry = findEntryById(ctx.entries, id);
                                            return entry?.ref;
                                        }
                                    }),
                                    'resultToast'
                                ]
                            },
                            'ENTRY.DELETE': {
                                actions: [
                                    assign((_, {id}) => {
                                        spawn(
                                            deleteEntryMachine.withContext({id}),
                                            `delete-entry-${id}`
                                        );
                                    }),
                                    'resultToast'
                                ]
                            },
                            'ENTRY.DELETE_SUCCESS': {
                                actions: [
                                    assign({
                                        entries: (ctx, {id}) => {
                                            return ctx.entries.filter((entry) => entry.data.id !== id);
                                        }
                                    }),
                                    'resultToast'
                                ]
                            },
                            'ENTRY.DELETE_FAILURE': {
                                actions: [
                                    send('IDLE', {
                                        to: (ctx, {id}) => {
                                            const entry = findEntryById(ctx.entries, id);
                                            return entry?.ref;
                                        }
                                    }),
                                    'resultToast'
                                ]
                            },
                            'ENTRY.REFRESH_TITLE': {
                                actions: assign((_, {id}) => {
                                    spawn(
                                        refreshTitleMachine.withContext({id}),
                                        `refresh-entry-title-${id}`
                                    );
                                })
                            },
                            'ENTRY.REFRESH_TITLE_SUCCESS': {
                                actions: [
                                    assign({
                                        entries: (ctx, {id, data: refreshedEntry}) => {
                                            return ctx.entries.map((currentEntry) => {
                                                // by default return the existing value
                                                let result = currentEntry;

                                                // if we found then entry that has been mutated
                                                if (currentEntry.data.id === id) {
                                                    // replace the entry in context with a union/clone
                                                    // of the existing entry and the new entry.
                                                    // this is important as we need to retain the `.ref`
                                                    // property on the entry, which is a spawned child actor/machine.
                                                    result = mergeEntries(currentEntry, refreshedEntry);
                                                }

                                                return result;
                                            });
                                        }
                                    }),
                                    send('IDLE', {
                                        to: (ctx, {id}) => {
                                            const entry = findEntryById(ctx.entries, id);
                                            return entry?.ref;
                                        }
                                    }),
                                    'resultToast'
                                ]
                            },
                            'ENTRY.REFRESH_TITLE_FAILURE': {
                                actions: [
                                    send('IDLE', {
                                        to: (ctx, {id}) => {
                                            const entry = findEntryById(ctx.entries, id);
                                            return entry?.ref;
                                        }
                                    }),
                                    'resultToast'
                                ]
                            },
                            'ENTRY.SHOW_QR_OVERLAY': {
                                target: 'qr_overlay_open',
                                actions: assign((ctx, {id}) => ({
                                    qrCodeEntry: findEntryById(ctx.entries, id)
                                }))
                            },
                            'ENTRY.INTENT_RENAME': {
                                target: 'rename_entry_modal_open',
                                actions: assign((ctx, {id}) => ({
                                    editingEntry: findEntryById(ctx.entries, id)
                                }))
                            },
                            'ENTRY.UPDATE_META': {
                                actions: [
                                    assign({
                                        entries: (ctx, {id, meta}) => {
                                            return ctx.entries.map((currentEntry) => {
                                                let result = currentEntry;
                                                if (currentEntry.data.id === id) {
                                                    result = mergeEntries(currentEntry, {
                                                        meta: [ ...meta ]
                                                    });
                                                }
                                                return result;
                                            });
                                        }
                                    }),
                                    'resultToast'
                                ]
                            },
                            'ENTRY.BUSY': {
                                actions: [
                                    send('BUSY', {
                                        to: (ctx, {id}) => {
                                            const entry = findEntryById(ctx.entries, id);
                                            return entry?.ref;
                                        }
                                    })
                                ]
                            },
                            'ENTRY.IDLE': {
                                actions: [
                                    send('IDLE', {
                                        to: (ctx, {id}) => {
                                            const entry = findEntryById(ctx.entries, id);
                                            return entry?.ref;
                                        }
                                    })
                                ]
                            },
                            'ENTRY.MOVED': {
                                actions: [
                                    assign({
                                        entries: (ctx, {id}) => {
                                            return ctx.entries.filter((entry) => entry.data.id !== id);
                                        }
                                    }),
                                    'resultToast'
                                ]
                            },
                            'SHOW_CREATE_FOLDER_MODAL': {
                                target: 'new_folder_modal_open',
                            },
                            'SHOW_RENAME_FOLDER_MODAL': {
                                target: 'rename_folder_modal_open',
                            },
                            'FOLDER.CREATED': {
                                actions: [
                                    assign({
                                        folders: (ctx, {folder}) => {
                                            return [
                                                ...ctx.folders,
                                                folder
                                            ];
                                        }
                                    }),
                                    'resultToast'
                                ]
                            },
                            'FOLDER.MOVED': {
                                actions: [
                                    assign({
                                        folders: ({folders}, {folderUUID}) => {
                                            return folders.filter((f => f.uuid !== folderUUID));
                                        }
                                    }),
                                    'resultToast'
                                ]
                            },
                            'FOLDER.RENAMED': {
                                actions: [
                                    assign({
                                        currentFolder: (ctx, {folder}) => {
                                            return {
                                                ...ctx.currentFolder,
                                                title: folder.title
                                            };
                                        }
                                    }),
                                    'resultToast'
                                ]
                            },
                            'FOLDER.BUSY': {
                                actions: assign({
                                    folders: ({folders}, {folderUUID}) => {
                                        return folders.map((folder) => {
                                            // by default return the existing value
                                            let result = folder;
                                            // if we found then folder that has been mutated
                                            if (folder.uuid === folderUUID) {
                                                result = {
                                                    ...folder,
                                                    isBusy: true
                                                };
                                            }
                                            return result;
                                        });
                                    }
                                })
                            },
                            'FOLDER.BUSY_END': {
                                actions: assign({
                                    folders: ({folders}, {folderUUID}) => {
                                        return folders.map((folder) => {
                                            // by default return the existing value
                                            let result = folder;
                                            // if we found then folder that has been mutated
                                            if (folder.uuid === folderUUID) {
                                                result = {
                                                    ...folder,
                                                    isBusy: false
                                                };
                                            }
                                            return result;
                                        });
                                    }
                                })
                            }
                        }
                    },
                    new_folder_modal_open: {
                        entry: ['addNoScrollBody'],
                        exit: ['removeNoScrollBody'],
                        on: {
                            'MODAL_CLOSE': {
                                target: 'idle'
                            }
                        }
                    },
                    rename_folder_modal_open: {
                        entry: ['addNoScrollBody'],
                        exit: ['removeNoScrollBody'],
                        on: {
                            'MODAL_CLOSE': {
                                target: 'idle'
                            }
                        }
                    },
                    rename_entry_modal_open: {
                        entry: ['addNoScrollBody'],
                        exit: [
                            'removeNoScrollBody',
                        ],
                        on: {
                            'MODAL_CLOSE': {
                                target: 'idle',
                                actions: [
                                    send('IDLE', {
                                        to: (ctx) => {
                                            const entryId = ctx.editingEntry.data.id;
                                            const entry = findEntryById(ctx.entries, entryId);
                                            return entry?.ref;
                                        }
                                    }),
                                ]
                            }
                        }
                    },
                    qr_overlay_open: {
                        entry: ['addNoScrollBody'],
                        exit: ['removeNoScrollBody'],
                        on: {
                            'CLOSE_QR_OVERLAY': {
                                target: 'idle',
                                actions: assign({
                                    qrCodeEntry: () => null
                                })
                            }
                        }
                    }
                }
            }
        }
    }, {
        ...machineOptions
    });
};

export {createFolderIndexMachine};

